<template>
  <div class="contact-page">

    <!-- Contact Info Cards -->
    <section class="contact-info py-8">
      <div class="container">
        <div class="info-grid">
          <div class="info-card reveal" v-for="(info, index) in contactInfo" :key="index" :style="{ transitionDelay: (index * 0.15) + 's' }">
            <div class="info-icon">
              <i :class="info.icon"></i>
            </div>
            <div class="info-content">
              <h3>{{ $t(info.titleKey) }}</h3>
              <p>{{ info.valueKey ? $t(info.valueKey) : info.value }}</p>
              <span class="info-label">{{ $t(info.labelKey) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Form Section -->
    <section class="contact-form-section py-8 bg-light">
      <div class="container">
        <div class="form-wrapper">
          <div class="form-header reveal">
            <span class="section-tag">
              <i class="fas fa-paper-plane mr-2"></i>{{ $t('contact.form.title') }}
            </span>
            <h2 class="section-title">{{ $t('contact.form.title') }}</h2>
            <p class="section-subtitle">{{ $t('contact.form.subtitle') }}</p>
          </div>

          <form @submit.prevent="submitForm" class="contact-form reveal">
            <div class="form-row">
              <div class="form-group" :class="{ 'has-error': errors.name, 'has-success': success.name }">
                <label for="name">
                  <i class="fas fa-user mr-2"></i>{{ $t('contact.form.name') }} <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="name"
                  v-model="form.name"
                  @focus="focusField('name')"
                  @blur="validateField('name')"
                  :placeholder="$t('contact.form.namePlaceholder')"
                >
                <span class="error-message" v-if="errors.name">{{ errors.name }}</span>
                <span class="success-icon" v-if="success.name"><i class="fas fa-check"></i></span>
              </div>
              <div class="form-group" :class="{ 'has-error': errors.phone, 'has-success': success.phone }">
                <label for="phone">
                  <i class="fas fa-phone mr-2"></i>{{ $t('contact.form.phone') }} <span class="required">*</span>
                </label>
                <input
                  type="tel"
                  class="form-control"
                  id="phone"
                  v-model="form.phone"
                  @focus="focusField('phone')"
                  @blur="validateField('phone')"
                  :placeholder="$t('contact.form.phonePlaceholder')"
                >
                <span class="error-message" v-if="errors.phone">{{ errors.phone }}</span>
                <span class="success-icon" v-if="success.phone"><i class="fas fa-check"></i></span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group" :class="{ 'has-success': form.email && !errors.email }">
                <label for="email">
                  <i class="fas fa-envelope mr-2"></i>{{ $t('contact.form.email') }}
                </label>
                <input
                  type="email"
                  class="form-control"
                  id="email"
                  v-model="form.email"
                  @blur="validateEmail"
                  :placeholder="$t('contact.form.emailPlaceholder')"
                >
                <span class="success-icon" v-if="form.email && !errors.email"><i class="fas fa-check"></i></span>
              </div>
              <div class="form-group">
                <label for="company">
                  <i class="fas fa-building mr-2"></i>{{ $t('contact.form.company') }}
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="company"
                  v-model="form.company"
                  :placeholder="$t('contact.form.companyPlaceholder')"
                >
              </div>
            </div>

            <div class="form-row">
              <div class="form-group full-width">
                <label for="subject">
                  <i class="fas fa-tag mr-2"></i>{{ $t('contact.form.subject') }} <span class="required">*</span>
                </label>
                <select
                  class="form-control"
                  id="subject"
                  v-model="form.subject"
                  @change="validateSubject"
                >
                  <option value="">{{ $t('contact.form.subjectPlaceholder') }}</option>
                  <option value="product">{{ $t('contact.form.subjectOptions.product') }}</option>
                  <option value="quote">{{ $t('contact.form.subjectOptions.quote') }}</option>
                  <option value="cooperation">{{ $t('contact.form.subjectOptions.cooperation') }}</option>
                  <option value="feedback">{{ $t('contact.form.subjectOptions.feedback') }}</option>
                  <option value="other">{{ $t('contact.form.subjectOptions.other') }}</option>
                </select>
                <span class="error-message" v-if="errors.subject">{{ errors.subject }}</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group full-width" :class="{ 'has-error': errors.message }">
                <label for="message">
                  <i class="fas fa-comment mr-2"></i>{{ $t('contact.form.message') }} <span class="required">*</span>
                </label>
                <textarea
                  class="form-control"
                  id="message"
                  v-model="form.message"
                  rows="5"
                  @focus="focusField('message')"
                  @blur="validateField('message')"
                  :placeholder="$t('contact.form.messagePlaceholder')"
                ></textarea>
                <div class="textarea-footer">
                  <span class="char-count" :class="{ 'warning': form.message.length > 450 }">
                    {{ form.message.length }} / 500
                  </span>
                </div>
                <span class="error-message" v-if="errors.message">{{ errors.message }}</span>
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn btn-primary btn-lg" :disabled="isSubmitting">
                <span v-if="!isSubmitting">
                  <i class="fas fa-paper-plane mr-2"></i>{{ $t('contact.form.submit') }}
                </span>
                <span v-else class="submitting">
                  <i class="fas fa-spinner fa-spin mr-2"></i>{{ $t('contact.form.submitting') }}
                </span>
              </button>
              <button type="button" class="btn btn-outline-secondary btn-lg" @click="resetForm">
                <i class="fas fa-redo mr-2"></i>{{ $t('contact.form.reset') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>

    <!-- Map Section -->
    <section class="map-section py-8">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-map-marker-alt mr-2"></i>{{ $t('contact.map.title') }}
          </span>
          <h2 class="section-title">{{ $t('contact.map.title') }}</h2>
        </div>

        <div class="map-wrapper reveal">
          <div class="map-placeholder">
            <i class="fas fa-map-marked-alt fa-5x"></i>
            <h4>{{ $t('contact.map.companyName') }}</h4>
            <p>{{ $t('contact.map.address') }}</p>
            <div class="map-info">
              <span><i class="fas fa-phone mr-2"></i>+86 15163903660</span>
              <span><i class="fas fa-envelope mr-2"></i>postmaster5@lyyllb.com</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Success Modal -->
    <div class="success-modal" v-if="showSuccessModal" @click.self="closeSuccessModal">
      <div class="success-content">
        <div class="success-icon">
          <i class="fas fa-check"></i>
        </div>
        <h3>提交成功！</h3>
        <p>感谢您的留言，我们将在24小时内与您联系。</p>
        <button class="btn btn-primary" @click="closeSuccessModal">
          <i class="fas fa-check mr-2"></i>确定
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Contact',
  data() {
    return {
      parallaxOffset: 0,
      pageLoaded: false,
      isSubmitting: false,
      showSuccessModal: false,
      form: {
        name: '',
        phone: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      },
      errors: {},
      success: {},
      contactInfo: [
        {
          icon: 'fas fa-map-marker-alt',
          titleKey: 'contact.info.address.title',
          valueKey: 'contact.info.address.value',
          labelKey: 'contact.info.address.label'
        },
        {
          icon: 'fas fa-phone-alt',
          titleKey: 'contact.info.phone.title',
          value: '+86 15163903660',
          labelKey: 'contact.info.phone.label'
        },
        {
          icon: 'fas fa-envelope',
          titleKey: 'contact.info.email.title',
          value: 'postmaster5@lyyllb.com',
          labelKey: 'contact.info.email.label'
        }
        // ,
        // {
        //   icon: 'fab fa-whatsapp',
        //   title: 'WhatsApp',
        //   value: '+86 15163903660',
        //   label: '快速咨询'
        // }
      ]
    }
  },
  mounted() {
    setTimeout(() => {
      this.pageLoaded = true
    }, 200)

    window.addEventListener('scroll', this.handleScroll)
    this.initRevealAnimations()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.parallaxOffset = window.scrollY * 0.3
    },
    focusField(field) {
      this.$set(this.errors, field, '')
    },
    validateField(field) {
      switch(field) {
        case 'name':
          if (!this.form.name.trim()) {
            this.$set(this.errors, 'name', '请输入您的姓名')
            this.$set(this.success, 'name', false)
          } else if (this.form.name.length < 2) {
            this.$set(this.errors, 'name', '姓名至少2个字符')
            this.$set(this.success, 'name', false)
          } else {
            this.$set(this.errors, 'name', '')
            this.$set(this.success, 'name', true)
          }
          break
        case 'phone':
          if (!this.form.phone.trim()) {
            this.$set(this.errors, 'phone', '请输入联系电话')
            this.$set(this.success, 'phone', false)
          } else if (!/^[\d\s\-\+\(\)]+$/.test(this.form.phone)) {
            this.$set(this.errors, 'phone', '请输入有效的电话号码')
            this.$set(this.success, 'phone', false)
          } else {
            this.$set(this.errors, 'phone', '')
            this.$set(this.success, 'phone', true)
          }
          break
        case 'message':
          if (!this.form.message.trim()) {
            this.$set(this.errors, 'message', '请输入留言内容')
          } else if (this.form.message.length < 10) {
            this.$set(this.errors, 'message', '留言内容至少10个字符')
          } else if (this.form.message.length > 500) {
            this.$set(this.errors, 'message', '留言内容不能超过500个字符')
            this.form.message = this.form.message.substring(0, 500)
          } else {
            this.$set(this.errors, 'message', '')
          }
          break
      }
    },
    validateEmail() {
      if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.$set(this.errors, 'email', '请输入有效的邮箱地址')
      } else {
        this.$set(this.errors, 'email', '')
      }
    },
    validateSubject() {
      if (!this.form.subject) {
        this.$set(this.errors, 'subject', '请选择留言主题')
      } else {
        this.$set(this.errors, 'subject', '')
      }
    },
    validateForm() {
      this.validateField('name')
      this.validateField('phone')
      this.validateSubject()
      this.validateField('message')

      return !Object.values(this.errors).some(error => error)
    },
    async submitForm() {
      if (!this.validateForm()) {
        return
      }

      this.isSubmitting = true

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))

      this.isSubmitting = false
      this.showSuccessModal = true
      this.resetForm()
    },
    resetForm() {
      this.form = {
        name: '',
        phone: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      }
      this.errors = {}
      this.success = {}
    },
    closeSuccessModal() {
      this.showSuccessModal = false
    },
    initRevealAnimations() {
      const reveals = document.querySelectorAll('.reveal')

      const revealOnScroll = () => {
        reveals.forEach(element => {
          const windowHeight = window.innerHeight
          const elementTop = element.getBoundingClientRect().top
          const revealPoint = 100

          if (elementTop < windowHeight - revealPoint) {
            element.classList.add('active')
          }
        })
      }

      window.addEventListener('scroll', revealOnScroll)
      window.addEventListener('load', revealOnScroll)

      revealOnScroll()
    }
  }
}
</script>

<style scoped>
.contact-page {
  padding-top: 120px;
}

/* Page Header */
.page-header {
  position: relative;
  height: 400px;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.page-header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background: linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 100%),
              url('https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=Contact%20us%20business%20communication%20background&image_size=landscape_16_9');
  background-size: cover;
  background-position: center;
  will-change: transform;
}

.page-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%);
}

.page-header-content {
  position: relative;
  z-index: 2;
  width: 100%;
}

.page-header-text {
  text-align: center;
  color: white;
}

.page-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 15px;
}

.page-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-title.animate-in span {
  opacity: 1;
  transform: translateY(0);
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;
  transition-delay: 0.3s;
}

.page-subtitle.animate-in {
  opacity: 0.9;
  transform: translateY(0);
}

/* Section Styles */
.section-tag {
  display: inline-block;
  padding: 8px 20px;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  color: #e53935;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 15px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 50px;
}

/* Contact Info Cards */
.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
}

.info-card {
  background: white;
  padding: 35px 25px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
}

.info-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #e53935, #ff6f00);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}

.info-card:hover::before {
  transform: scaleX(1);
}

.info-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 60px rgba(229, 57, 53, 0.15);
}

.info-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #e53935;
  transition: all 0.4s ease;
}

.info-card:hover .info-icon {
  background: linear-gradient(135deg, #e53935, #c62828);
  color: white;
  transform: scale(1.1);
}

.info-content h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.info-content p {
  font-size: 1.1rem;
  color: #e53935;
  font-weight: 600;
  margin-bottom: 8px;
}

.info-label {
  font-size: 0.85rem;
  color: #999;
}

/* Form Section */
.form-wrapper {
  background: white;
  border-radius: 24px;
  padding: 60px;
  box-shadow: 0 10px 50px rgba(0,0,0,0.05);
}

.form-header {
  text-align: center;
  margin-bottom: 50px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

.form-group {
  position: relative;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: #555;
  margin-bottom: 10px;
}

.form-group label i {
  color: #e53935;
}

.required {
  color: #e53935;
}

.form-control {
  width: 100%;
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  color: #333;
  transition: all 0.3s ease;
  background: #fafafa;
}

.form-control::placeholder {
  color: #aaa;
}

.form-control:focus {
  outline: none;
  border-color: #e53935;
  background: white;
  box-shadow: 0 0 0 4px rgba(229, 57, 53, 0.1);
}

textarea.form-control {
  resize: vertical;
  min-height: 150px;
}

.form-group.has-error .form-control {
  border-color: #e53935;
  background: #fff5f5;
}

.form-group.has-success .form-control {
  border-color: #4caf50;
  background: #f0fff0;
}

.error-message {
  display: block;
  font-size: 0.85rem;
  color: #e53935;
  margin-top: 8px;
}

.success-icon {
  position: absolute;
  right: 15px;
  top: 45px;
  color: #4caf50;
  font-size: 1.1rem;
}

.textarea-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.char-count {
  font-size: 0.85rem;
  color: #999;
}

.char-count.warning {
  color: #e53935;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 40px;
}

.btn-primary {
  background: linear-gradient(135deg, #e53935, #c62828);
  border: none;
  padding: 16px 50px;
  font-size: 1.1rem;
  font-weight: 500;
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(229, 57, 53, 0.4);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-outline-secondary {
  background: transparent;
  border: 2px solid #ddd;
  padding: 16px 50px;
  font-size: 1.1rem;
  font-weight: 500;
  border-radius: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-outline-secondary:hover {
  border-color: #e53935;
  color: #e53935;
}

/* Map Section */
.map-wrapper {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 50px rgba(0,0,0,0.05);
}

.map-placeholder {
  padding: 100px 50px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  text-align: center;
  color: #666;
}

.map-placeholder i {
  color: #e53935;
  margin-bottom: 30px;
}

.map-placeholder h4 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.map-placeholder p {
  font-size: 1.1rem;
  margin-bottom: 30px;
}

.map-info {
  display: flex;
  gap: 40px;
  justify-content: center;
  flex-wrap: wrap;
}

.map-info span {
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  color: #666;
}

.map-info i {
  color: #e53935;
}

/* Success Modal */
.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.success-content {
  background: white;
  border-radius: 24px;
  padding: 60px;
  text-align: center;
  max-width: 450px;
  animation: bounceIn 0.5s ease;
}

.success-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  animation: pulse 1s infinite;
}

.success-icon i {
  font-size: 3rem;
  color: white;
}

.success-content h3 {
  font-size: 1.8rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}

.success-content p {
  font-size: 1rem;
  color: #666;
  margin-bottom: 30px;
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  70% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

/* Utilities */
.py-8 {
  padding-top: 80px;
  padding-bottom: 80px;
}

/* Reveal Animations */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 991px) {
  .info-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-wrapper {
    padding: 40px 30px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions .btn {
    width: 100%;
  }
}

@media (max-width: 767px) {
  .page-title {
    font-size: 2.5rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .form-wrapper {
    padding: 30px 20px;
  }

  .success-content {
    padding: 40px 30px;
    margin: 20px;
  }
}
</style>
